import { createAsyncThunk } from '@reduxjs/toolkit';
import PortalService from '@/services/PortalService';
import LocationsService from '@/services/LocationsService';
import CompanyService from '@/services/CompanyService';

const handler = (name, asyncFunction) =>
  createAsyncThunk(`Portal/${name}`, async (arg) => {
    const data = await asyncFunction(arg);
    return data;
  });

export const getPortalBasicSettings = handler(
  'getPortalBasicSettings',
  async ({ id, subId }) => {
    const data = await PortalService.getPortalBasicSettings(id, subId);
    return data;
  }
);

export const updatePortalBasicSettings = handler(
  'updatePortalBasicSettings',
  async ({ portalId, subportalId, config, value }) => {
    await PortalService.updatePortalBasicSettings(
      portalId,
      subportalId,
      config,
      value
    );
    return { config, value };
  }
);

export const getPortalConfigurations = handler(
  'getPortalConfigurations',
  async ({ portalId, subportalId }) => {
    const data = await PortalService.getPortalConfigurations(
      portalId,
      subportalId
    );
    return data;
  }
);

export const updatePortalConfigurations = handler(
  'updatePortalConfigurations',
  async (params) => {
    const data = await PortalService.updatePortalConfigurations(params);
    return data;
  }
);

export const getPortalRulesSummary = handler(
  'getPortalRulesSummary',
  async ({ portalId, subportalId }) => {
    const data = await PortalService.getPortalRulesSummary(
      portalId,
      subportalId
    );
    return data;
  }
);

export const getPortalStarringRules = handler(
  'getPortalStarringRules',
  async ({ portalId, page, subportalId }) => {
    const data = await PortalService.getPortalStarringRules(
      portalId,
      subportalId,
      page
    );
    return data;
  }
);

export const createRule = handler(
  'createRule',
  async ({ portalId, ruleData, subportalId }) => {
    const data = await PortalService.createRule(
      portalId,
      ruleData,
      subportalId
    );
    return data;
  }
);

export const updateRule = handler(
  'updateRule',
  async ({ ruleId, portalId, requestBody, subportalId }) => {
    const data = await PortalService.updateRule(
      ruleId,
      portalId,
      requestBody,
      subportalId
    );
    return data;
  }
);

export const patchRule = handler(
  'patchRule',
  async ({ ruleId, portalId, requestBody, subportalId }) => {
    const data = await PortalService.patchRule(
      ruleId,
      portalId,
      requestBody,
      subportalId
    );
    return data;
  }
);

export const getRule = handler(
  'getRule',
  async ({ ruleId, portalId, subportalId }) => {
    const data = await PortalService.getRule(ruleId, portalId, subportalId);
    return data;
  }
);

export const deleteRule = createAsyncThunk(
  'Portal/deleteRule',
  async ({ ruleId, portalId, subportalId }, thunkAPI) => {
    const data = await PortalService.deleteRule(ruleId, portalId, subportalId);
    try {
      await thunkAPI
        .dispatch(getPortalRulesSummary({ portalId, subportalId }))
        .unwrap();
    } catch (e) {
      console.error('Error refreshing portal rules summary:', e);
    }
    return data;
  }
);

export const getPropertiesForPage = handler(
  'getProperties',
  async ({ propsIds, page }) => {
    const itemsPerPage = 10;
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const pageIds = propsIds.slice(startIndex, endIndex);

    if (pageIds.length === 0) return { properties: [] };
    const data = PortalService.getProperties(pageIds.join(','));
    return data;
  }
);

export const getPropIdsMatchingCriteria = handler(
  'getPropIdsMatchingCriteria',
  async ({ portalId, subportalId, requestBody }) => {
    const data = PortalService.getPropIdsMatchingCriteria(
      portalId,
      subportalId,
      requestBody
    );
    return data;
  }
);

export const getDevelopmentsForPage = handler(
  'getDevelopments',
  async ({ devIds, page }) => {
    const itemsPerPage = 10;
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const pageIds = devIds.slice(startIndex, endIndex);

    if (pageIds.length === 0) return { developments: [] };
    const data = PortalService.getDevelopments(pageIds.join(','));
    return data;
  }
);

export const updateStarringRulesOrder = handler(
  'updateStarringRulesOrder',
  async ({ idsArray, portalId, subportalId }) => {
    const data = await PortalService.updateOrder(
      idsArray,
      portalId,
      'starred',
      subportalId
    );
    return data;
  }
);

export const getAttributesForCriteria = handler(
  'getAttributesForCriteria',
  async ({ portalId, subportalId }) => {
    const data = await PortalService.getAttributesForCriteria(
      portalId,
      subportalId
    );
    return data;
  }
);

export const getSelectOptions = handler(
  'getSelectOptions',
  async ({ valueApiPath, attributeId }) => {
    const data = await PortalService.getSelectOptions(valueApiPath);
    const raw = data.results || [];

    // TODO: Backend should return filtered options
    const filtered = raw.filter((item) => item && item.name !== '---');
    return { attributeId, options: filtered };
  }
);

export const getDevelopmentTypes = handler('getDevelopmentTypes', async () => {
  const data = await PortalService.getDevelopmentTypes();
  return data;
});

export const searchLocationDivisions = handler(
  'searchLocationDivisions',
  async (pattern) => {
    const data = await LocationsService.searchLocationDivisions(pattern);
    return data;
  }
);

export const getCustomTagsSearch = handler(
  'getCustomTagsSearch',
  async (params) => {
    const data = await PortalService.getCustomTagsSearch(params);
    return data;
  }
);

export const requestSmartPublishingRulesUpgrade = handler(
  'requestSmartPublishingRulesUpgrade',
  async () => {
    const data = await PortalService.requestSmartPublishingRulesUpgrade();
    return data;
  }
);

export const getUpgradePending = handler('getUpgradePending', async () => {
  const data = await CompanyService.getUpgradePending();
  return data;
});

export const getImportedFromOrigins = handler(
  'getImportedFromOrigins',
  async () => {
    const data = await PortalService.getImportedFromOrigins();
    return data;
  }
);

export const getXmlFiles = handler('getXmlFiles', async ({ subportalId }) => {
  const data = await PortalService.getXmlFiles(subportalId);
  return data;
});

export const addXmlFile = handler('addXmlFile', async (payload) => {
  const data = await PortalService.addXmlFile(payload);
  return data;
});

export const deleteXmlFile = handler('deleteXmlFile', async ({ fileId }) => {
  const data = await PortalService.deleteXmlFile(fileId);
  return data;
});

export const getXmlFileHistory = handler(
  'getXmlFileHistory',
  async ({ subportalId, xmlFileId } = {}) => {
    const data = await PortalService.getXmlFileHistory(subportalId, xmlFileId);
    return data;
  }
);
